/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { ComponentProps, SVGProps } from 'react';
import styled from 'styled-components';
import FilterDropdown from './FilterDropdown';
import { InputButton, InputWithIcon } from './Input';
import { MagnifyingIcon } from './SearchField';

export interface ResourceFilteringProps {
    resourceCount: number;
    perPage: number;
    typeCounts: Record<string, number>;
    selectedTypes: string[];
    query: string;
    onChange: (types: string[], count: number, query: string) => any;
}

const CrossSVG = styled.svg`
  margin-right: 12px;
`;

const Cross = (props: ComponentProps<typeof CrossSVG>) => (
  <CrossSVG
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 12 12"
    width="0.66em"
    height="0.66em"
    {...props}
  >
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M.4.4a1 1 0 0 1 1.2 0L6 4.8 10.4.4a.8.8 0 1 1 1.2 1.2L7.2 6l4.4 4.4a.8.8 0 0 1-1.2 1.2L6 7.2l-4.4 4.4a.8.8 0 1 1-1.2-1.2L4.8 6 .4 1.6A.8.8 0 0 1 .4.4Z"
      clipRule="evenodd"
    />
  </CrossSVG>
);

export const LargeCross = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 14 14"
    height="1em"
    {...props}
  >
    <path
      fill="#2E363D"
      fillRule="evenodd"
      d="M.3.3a1 1 0 0 1 1.4 0L7 5.58l5.3-5.3a1 1 0 1 1 1.4 1.42L8.42 7l5.3 5.3a1 1 0 0 1-1.42 1.4L7 8.42l-5.3 5.3A1 1 0 0 1 .3 12.3L5.58 7 .29 1.7A1 1 0 0 1 .3.3Z"
      clipRule="evenodd"
    />
  </svg>
);
const ResourceFilteringContainer = styled.section`
  border-radius: 16px;
  background-color: white;
  border: 1px solid var(--color-charcoal-v10);
  box-shadow: ${(props) => props.theme.shadows.hard};
`;

const Radio = styled.input.attrs({ type: 'radio' })`
  appearance: none;
  margin: 0;
  + label:not(:last-child) {
    margin-right: 16px;
  }

  &:checked {
    + label {
      color: var(--color-bluescape-v100);
      font-weight: 600;
    }
  }
`;

const Label = styled.label`
  flex: 1;
  
`;

const CountContainer = styled.div`
  color: var(--color-charcoal-v100);
  font-size: 15px;
`;

const FilterContainer = styled.div`
  margin: 24px;
  display: flex;
  gap: 16px;

  @media (max-width: ${(props) => props.theme.breakpoints.medium}) {
    > :last-child {
      flex: 2;
    }
    gap: 16px;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.small}) {
    flex-direction: column;
    gap: 24px;
  }
`;

const FilterTypeTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 12px;
`;

const VerticalDivider = styled.div`
  display: inline;
  border-left: 1px solid var(--color-charcoal-v20);
  height: 24px;
  margin: 0 12px;
  /* width: 0px; */
`;

const RightAlign = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  > div {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    > :last-child {
      display: inline-flex;
      height: 2.2rem;
      align-items: center;
      justify-content: center;
    }
  }

  @media (max-width: ${(props) => props.theme.breakpoints.small}) {
    align-items: flex-start;
  }
`;

const FilterItem = styled.div`
  display: flex;
  align-items: center;
  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const FilterCheckbox = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  width: 24px;
  
  > *:not(:last-child) {
    margin-bottom: 6px;
  }

  margin: 0;
  height: 24px;
  border: 2px solid var(--color-charcoal-v30);
  border-radius: 4px;
  cursor: pointer;

  + label {
    cursor: pointer;
    padding-left: 10px;
  }

  &:checked {
    border-color: var(--color-bluescape-v100);
    background-color: var(--color-bluescape-v100);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 10'%3E%3Cpath fill='white' d='M13.7.3c.4.3.4 1 0 1.4l-7.5 8a1 1 0 0 1-1.4 0l-4.5-4a1 1 0 0 1 1.4-1.4l3.7 3.3L12.3.3a1 1 0 0 1 1.4 0Z'/%3E%3C/svg%3E%0A");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 14px;
  }
`;

const ClearButton = styled.button.attrs({ type: 'button' })`
  appearance: none;
  border: none;
  background-color: transparent;
  cursor: pointer;
  border-top: 1px solid var(--color-charcoal-v20);
  font-size: inherit;
  display: block;
  width: calc(100% + 32px);
  margin: 0 -16px;
  padding: 0 22px;
  padding-top: 11px;
  color: var(--color-bluescape-v100);
  text-align: start;
  font-weight: 600;
  font-family: inherit;

  &:disabled {
    color: var(--color-charcoal-v50);
    cursor: unset;
  }
`;

const LinkButton = styled.button.attrs({ type: 'button' })`
  appearance: none;
  border: none;
  background-color: transparent;
  cursor: pointer;
  font-size: inherit;
  display: block;
  padding: 0;
  display: inline-block;
  font-weight: 600;
  
  color: var(--color-bluescape-v100);
`;

const Flex1 = styled.div`
  flex: 1;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  color: var(--color-charcoal-v100);
  margin-bottom: 32px;
`;

const SearchText = styled.p`
  margin: 0;
  font-size: 18px;

  strong {
    font-weight: 600;
  }
`;

const ResourceFiltering: React.FC<ResourceFilteringProps> = (props) => {
  const [selectedTypes, setSelectedTypes] = React.useState(props.selectedTypes);
  const [perPage, setPerPage] = React.useState(props.perPage);
  const [query, setQuery] = React.useState(props.query);
  const [searching, setSearching] = React.useState(false);
  const { typeCounts, onChange, resourceCount } = props;
  const types = Object.keys(typeCounts).sort();

  const resourceCountStr = `${resourceCount} item${resourceCount !== 1 ? 's' : ''}`;

  const handleChange = (newTypes?: string[], count?: number, newQuery?: string) => {
    setSearching(false);
    onChange(newTypes ?? selectedTypes, count ?? perPage, newQuery ?? query);
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const count = parseInt(e.currentTarget.value, 10);
    setPerPage(count);
    handleChange(undefined, count);
  };

  const allChecked = selectedTypes.length === types.length || !selectedTypes.length;
  const anyChecked = selectedTypes.length > 0;
  const selectedTypesOrdered = types.filter((type) => selectedTypes.includes(type));
  const dropdownStr = allChecked ? 'All' : selectedTypesOrdered.map((type) => type.replace(/_/g, ' ')).join(', ');
  return (
    <Wrapper>
      <ResourceFilteringContainer>
        <form
          method="get"
          onSubmit={(e) => {
            e.preventDefault();
            handleChange();
          }}
        >
          <FilterContainer>
            <Flex1>
              <FilterTypeTitle>Filter by type</FilterTypeTitle>
              <FilterDropdown isFiltered={!allChecked} title={dropdownStr}>
                {(open) => (
                  <>
                    {types.map((type) => (
                      <FilterItem>
                        <FilterCheckbox
                          onChange={(e) => {
                            const { checked } = e.currentTarget;
                            const newSelectedTypes = checked
                              ? [...selectedTypes, type] : selectedTypes.filter((t) => t !== type);
                            setSelectedTypes(newSelectedTypes);
                            handleChange(newSelectedTypes);
                          }}
                          checked={selectedTypes.includes(type)}
                          disabled={!open}
                          name="type"
                          key={type}
                          value={type}
                          id={`type-${type}`}
                        />
                        <Label htmlFor={`type-${type}`}>
                          {type}
                          {' '}
                          (
                          {typeCounts[type]}
                          )
                        </Label>
                      </FilterItem>
                    ))}
                    <FilterItem>
                      <ClearButton
                        disabled={!anyChecked || !open}
                        onClick={
                      () => {
                        setSelectedTypes([]);
                        handleChange([]);
                      }
                    }
                      >
                        <Cross />
                        Clear filter
                      </ClearButton>
                    </FilterItem>
                  </>
                )}
              </FilterDropdown>
            </Flex1>
            <Flex1>
              <FilterTypeTitle>Search resources</FilterTypeTitle>
              <InputWithIcon
                name="q"
                value={query}
                onChange={(e) => {
                  setSearching(e.currentTarget.value !== query);
                  setQuery(e.currentTarget.value);
                }}
                low
                iconR={
                  (!query || searching) ? <InputButton type="submit"><MagnifyingIcon /></InputButton> : (
                    <InputButton
                      onClick={() => {
                        setQuery('');
                        handleChange();
                      }}
                    >
                      <LargeCross />
                    </InputButton>
                  )
                }
                placeholder="Search"
              />
            </Flex1>
            <RightAlign>
              <div>
                <FilterTypeTitle>Items per page</FilterTypeTitle>
                <CountContainer>
                  <span>
                    {resourceCountStr}
                  </span>
                  <VerticalDivider />
                  <Radio onChange={handleRadioChange} defaultChecked={perPage === 12} name="count" id="count-12" value="12" />
                  <Label htmlFor="count-12">12</Label>
                  <Radio onChange={handleRadioChange} defaultChecked={perPage === 24} name="count" id="count-24" value="24" />
                  <Label htmlFor="count-24">24</Label>
                  <Radio onChange={handleRadioChange} defaultChecked={perPage !== 12 && perPage !== 24} name="count" id="count-all" value="0" />
                  <Label htmlFor="count-all">All</Label>
                </CountContainer>
              </div>
            </RightAlign>
          </FilterContainer>
        </form>
      </ResourceFilteringContainer>
      {props.query && (
      <SearchText>
        <strong>
          Search for “
          {props.query}
          ”
        </strong>
        {' '}
        ·
        {' '}
        {resourceCount}
        {' '}
        result
        {resourceCount === 1 ? '' : 's'}
        {' '}
        ·
        {' '}
        <LinkButton onClick={() => { setQuery(''); handleChange(selectedTypes, perPage, ''); }}>Clear</LinkButton>
      </SearchText>
      )}
    </Wrapper>
  );
};

export default ResourceFiltering;
